export const parsedData = (data: any) => {
    try {
        if (typeof data === 'string') {
            try {
                return JSON.parse(data);
            } catch (parseError) {
                console.error("Failed to parse string data:", data, parseError);
                return null;
            }
        }
        return data;
    } catch (error) {
        console.error("Error parsing data:", error);
        return null;
    }
};


export const formatDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);

    return `${day}/${month}/${year}`;
}

// Format number to compact USD (e.g. $1.2K, $3.4M, $5.6B)
export const formatCurrencyCompact = (num: number | null | undefined, showDollar: boolean = true): string => {
    if (num == null || isNaN(num)) return '--';
    if (num === 0) return showDollar ? '$0' : '0';

    const absNum = Math.abs(num);

    // Handle very small numbers (< 0.01)
    if (absNum < 0.01) {
        const str = absNum.toString(); // e.g. "0.000003422"
        const zerosMatch = str.match(/0\.(0*)/);
        const zeroCount = zerosMatch ? zerosMatch[1].length : 0;

        const significantDigits = str.slice(2 + zeroCount).slice(0, 4); // 4 significant digits

        const prefix = showDollar ? '$' : '';

        // If there's only one zero after '.', show it directly
        if (zeroCount <= 1) {
            return `${prefix}0.${'0'.repeat(zeroCount)}${significantDigits}`;
        }

        // Otherwise show {n} notation for hidden zeros
        return `${prefix}0.0{${zeroCount - 1}}${significantDigits}`;
    }

    // Normal compact currency formatting
    if (showDollar) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            notation: 'compact',
            maximumFractionDigits: 4,
        }).format(num);
    } else {
        return new Intl.NumberFormat('en-US', {
            notation: 'compact',
            maximumFractionDigits: 4,
        }).format(num);
    }
};

// Format percentage with support for very small numbers (e.g. 0.0{5}3422)
export const formatPercentageCompact = (num: number | null | undefined): string => {
    if (num == null || isNaN(num)) return '--';
    if (num === 0) return '0';

    const absNum = Math.abs(num);
    const sign = num < 0 ? '-' : '';

    // Handle very small percentages (< 0.01)
    if (absNum < 0.01) {
        const str = absNum.toString(); // e.g. "0.000003422"
        const zerosMatch = str.match(/0\.(0*)/);
        const zeroCount = zerosMatch ? zerosMatch[1].length : 0;

        const significantDigits = str.slice(2 + zeroCount).slice(0, 4); // 4 significant digits

        // If there's only one zero after '.', show it directly
        if (zeroCount <= 1) {
            return `${sign}0.${'0'.repeat(zeroCount)}${significantDigits}`;
        }

        // Otherwise show {n} notation for hidden zeros
        return `${sign}0.0{${zeroCount - 1}}${significantDigits}`;
    }

    // Normal percentage formatting
    return `${sign}${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(absNum)}`;
};




export const formatDateLocale = (iso?: string | null): string => {
    if (!iso) return '--';
    const d = new Date(iso);
    return isNaN(d.getTime())
        ? '--'
        : d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        });
};

export const formatPercent = (num: number | null | undefined): string => {
    if (num == null || isNaN(num)) return '--';

    return `${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num)}%`;
};

export const formatVolume = (num: number | null | undefined): string => {
    if (num == null || isNaN(num)) return "--";

    // Use Intl.NumberFormat for compact notation (K, M, B, T)
    return new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 2,
    }).format(num);
}

// Return CSS class for positive / negative / stable trend
export const trendClass = (val: number | null): string => {
    if (val == null) return "stable";
    return val > 0 ? "positive" : val < 0 ? "negative" : "stable";
};

// Get last item from an array
export const last = <T>(arr?: T[]): T | undefined =>
    Array.isArray(arr) && arr.length ? arr[arr.length - 1] : undefined;

// Round number to 1 decimal place
export const round = (n: number | null | undefined): number | null =>
    n == null ? null : Math.round(n * 10) / 10;

// Convert USD amount to billions
export const toBillions = (usd: number | undefined | null): number | null =>
    typeof usd === "number" && isFinite(usd) ? usd / 1e9 : null;

export const trimText = (text: string | null | undefined, length: number = 40): string => {
    if (!text) return "-";
    return text.length > length ? text.slice(0, length) + "..." : text;
};